import React from 'react';
import { useQuery } from '@apollo/client';
import PropTypes from 'prop-types';

import SelectBox from '../atoms/form/SelectBox';
import RadioButtons from '../atoms/form/Radio';
import { FetchDirections } from '../../queries/index.graphql';
import { createPrefecturesArray } from '../../modules/flight';
import { validateNumber } from '../../utils';
import { getAddressByZipCode } from '../../modules/address';
import { CHARGE_KIND } from '../../constants';

const CustomerForm = ({ index, id, customer, representative, femaleOnly, isAna2, indicatorOptions, planOptionChargeKinds, setPlanOptionChargeKinds }) => {
  const { state, setters } = customer;
  const { data } = useQuery(FetchDirections);
  const directions = data ? data.directions : [];
  const prefectures = createPrefecturesArray(directions);
  const prefecturesZerofill = prefectures.map((prefecture) => ({
    ...prefecture,
    id: prefecture.id.toString().padStart(2, '0'),
  }));

  const maleDiabled = state.type === CHARGE_KIND.adult && femaleOnly;

  const genderOptions = [
    { id: 1, name: '男性', value: 'MALE', disabled: maleDiabled },
    { id: 2, name: '女性', value: 'FAMALE' },
  ];

  const phoneOptions = [
    { id: 'phone1', name: '自宅', value: 'HOME' },
    { id: 'phone2', name: '携帯電話', value: 'MOBILE' },
  ];

  const nameLabel = representative ? 'お名前' : 'お名前（カナ）';
  const firstNamePlaceHolder = representative ? '姓' : 'セイ';
  const lastNamePlaceHolder = representative ? '名' : 'メイ';
  const nameDesc = representative ? '漢字・ひらがな・英 20文字以内' : 'カタカナ 20文字以内';

  const isInfant = state.type === CHARGE_KIND.infant;
  const isChildren2 = state.type === CHARGE_KIND.children2;
  const infantIndicatorDisplay = isInfant || (!isAna2 && isChildren2);

  const handleChangeGender = (value, index, representative) => {
    setters.setGender(value);

    const customerIndex = representative ? 0 : index + 1;
    const newPlanOptionChargeKinds = planOptionChargeKinds.map((planOption) => {
      return planOption.map((kind) => {
        if (kind.sort === customerIndex) {
          return {
            ...kind,
            sex: genderOptions.find((option) => option.value === value).id
          };
        }
        return kind;
      });
    });
    setPlanOptionChargeKinds(newPlanOptionChargeKinds);
  }

  return (
    <React.Fragment>
      <div
        id={representative ? 'input.member.name.full' : `input.accompanies.${index}.name.full`}
        className="EntryFormBlock"
      >
        <label className="EntryLabel">{nameLabel}</label>
        <div className="inputAreaWrapper">
          <div className="inputArea">
            <div className="inputName">
              <div className="inputSizeName">
                <input
                  id={representative ? 'input.member.name.last' : `input.accompanies.${index}.name.last`}
                  type="text"
                  placeholder={firstNamePlaceHolder}
                  value={state.lastName || ''}
                  onChange={(e) => setters.setLastName(e.target.value)}
                />
              </div>
            </div>
            <div className="inputName">
              <div className="inputSizeName">
                <input
                  id={representative ? 'input.member.name.first' : `input.accompanies.${index}.name.first`}
                  type="text"
                  placeholder={lastNamePlaceHolder}
                  value={state.firstName || ''}
                  onChange={(e) => setters.setFirstName(e.target.value)}
                />
              </div>
            </div>
          </div>
          <div className="inputAreaSup">{nameDesc}</div>
        </div>
      </div>
      {representative && (
        <div id="input.member.phoneticName.full" className="EntryFormBlock">
          <label className="EntryLabel">フリガナ</label>
          <div className="inputAreaWrapper">
            <div className="inputArea">
              <div className="inputName">
                <div className="inputSizeName">
                  <input
                    id="input.member.phoneticName.last"
                    type="text"
                    placeholder="セイ"
                    value={state.lastPhoneticName || ''}
                    onChange={(e) => setters.setLastPhoneticName(e.target.value)}
                  />
                </div>
              </div>
              <div className="inputName">
                <div className="inputSizeName">
                  <input
                    id="input.member.phoneticName.first"
                    type="text"
                    placeholder="メイ"
                    value={state.firstPhoneticName || ''}
                    onChange={(e) => setters.setFirstPhoneticName(e.target.value)}
                  />
                </div>
              </div>
            </div>
            <div className="inputAreaSup">カタカナ 20文字以内</div>
          </div>
        </div>
      )}
      <div
        id={representative ? 'input.member.alphabetName.full' : `input.accompanies.${index}.alphabetName.full`}
        className="EntryFormBlock"
      >
        <label className="EntryLabel">ローマ字</label>
        <div className="inputAreaWrapper">
          <div className="inputArea">
            <div className="inputName">
              <div className="inputSizeName">
                <input
                  id={representative ? "input.member.alphabetName.last" : `input.accompanies.${index}.alphabetName.last`}
                  type="text"
                  placeholder="SEI"
                  value={state.lastAlphabetName || ''}
                  onChange={(e) => setters.setLastAlphabetName(e.target.value)}
                />
              </div>
            </div>
            <div className="inputName">
              <div className="inputSizeName">
                <input
                  id={representative ? "input.member.alphabetName.first" : `input.accompanies.${index}.alphabetName.first`}
                  type="text"
                  placeholder="MEI"
                  value={state.firstAlphabetName || ''}
                  onChange={(e) => setters.setFirstAlphabetName(e.target.value)}
                />
              </div>
            </div>
          </div>
          <div className="inputAreaSup">英字 15文字以内</div>
        </div>
      </div>
      <div
        id={representative ? 'input.member.chargeKind' : `input.accompanies.${index}.chargeKind`}
        className="EntryFormBlock"
      >
        <label className="EntryLabel">生年月日</label>
        <div
          className="inputAreaWrapper"
          id={representative ? 'input.member.birthday' : `input.accompanies.${index}.birthday`}
        >
          <div className="inputArea">
            <div className="inputName inputNameSide">
              <span className="inputNameSup">西暦</span>
              <div className="inputSizeNumber">
                <input
                  type="text"
                  placeholder="1980"
                  value={state.birthYear || ''}
                  onChange={(e) => {
                    const value = validateNumber(e.target.value);
                    setters.setBirthYear(value);
                  }}
                />
              </div>
              <span className="inputNameSup">年</span>
            </div>
            <div className="inputName inputNameSide">
              <div className="inputSizeNumber inputSizeNumberMin">
                <input
                  type="text"
                  placeholder="12"
                  value={state.birthMonth || ''}
                  onChange={(e) => {
                    const value = validateNumber(e.target.value);
                    setters.setBirthMonth(value);
                  }}
                />
              </div>
              <span className="inputNameSup">月</span>
            </div>
            <div className="inputName inputNameSide">
              <div className="inputSizeNumber inputSizeNumberMin">
                <input
                  type="text"
                  placeholder="23"
                  value={state.birthDate || ''}
                  onChange={(e) => {
                    const value = validateNumber(e.target.value);
                    setters.setBirthDate(value);
                  }}
                />
              </div>
              <span className="inputNameSup">日</span>
            </div>
          </div>
          <div className="inputAreaSup inputAreaSupBirth">半角数字</div>
        </div>
      </div>
      <div className="EntryFormBlock">
        <label className="EntryLabel">性別</label>
        <div
          className="inputAreaWrapper"
          id={representative ? 'input.member.sex' : `input.accompanies.${index}.sex`}
        >
          <div className="inputArea">
            <RadioButtons
              id={`gender-radio-${id}`}
              options={genderOptions}
              value={state.gender}
              onChange={(value) => handleChangeGender(value, index, representative)}
              withOutline
            />
          </div>
        </div>
      </div>
      {infantIndicatorDisplay && (
        <div className="EntryFormBlock">
          <label className="EntryLabel">付添人</label>
          <div
            className="inputAreaWrapper"
            id={`input.accompanies.${index}.infantIndicator`}
          >
            <div className="inputArea">
              <SelectBox
                options={indicatorOptions}
                value={state.infantIndicator}
                onChange={(e) => setters.setInfantIndicator(e.target.value)}
              />
            </div>
          </div>
        </div>
      )}
      {representative && (
        <div className="EntryFormBlock EntryFormBlockAlignTop">
          <label className="EntryLabel">日中ご連絡先</label>
          <div className="inputAreaWrapper">
            <RadioButtons
              id={`phone-radio-${id}`}
              options={phoneOptions}
              value={state.phoneType}
              onChange={(value) => setters.setPhoneType(value)}
              withOutline
            />
            <div id="input.member.contact.tel.full" className="inputArea inputAreaInner">
              <label className="innerLabel">電話番号</label>
              <div className="inputAreaInnerTel">
                <div className="inputName">
                  <div className="inputSizeNumber">
                    <input
                      id="input.member.contact.tel.first"
                      type="text"
                      placeholder="03"
                      value={state.phoneNumber1 || ''}
                      onChange={(e) => setters.setPhoneNumber1(e.target.value)}
                    />
                  </div>
                </div>
                <div className="inputSymbol">－</div>
                <div className="inputName">
                  <div className="inputSizeNumber">
                    <input
                      id="input.member.contact.tel.second"
                      type="text"
                      placeholder="1234"
                      value={state.phoneNumber2 || ''}
                      onChange={(e) => setters.setPhoneNumber2(e.target.value)}
                    />
                  </div>
                </div>
                <div className="inputSymbol">－</div>
                <div className="inputName">
                  <div className="inputSizeNumber">
                    <input
                      id="input.member.contact.tel.third"
                      type="text"
                      placeholder="5678"
                      value={state.phoneNumber3 || ''}
                      onChange={(e) => setters.setPhoneNumber3(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="inputAreaSup inputAreaSupInner">半角数字</div>
          </div>
        </div>
      )}
      {representative && (
        <React.Fragment>
          <div
            id="input.member.postalCode"
            className="EntryFormBlock"
          >
            <label className="EntryLabel">郵便番号</label>
            <div
              className="inputAreaWrapper"
            >
              <div className="inputArea">
                <div className="inputName inputNameSide">
                  <span className="inputNameSup">〒</span>
                  <div className="inputSizeNumber inputSizeNumberMin">
                    <input
                      type="text"
                      placeholder="123"
                      value={state.postalCode1 || ''}
                      onChange={(e) => {
                        const value = e.target.value || '';
                        const validatedValue = value.substring(0, 3);
                        setters.setPostalCode1(validatedValue);
                      }}
                    />
                  </div>
                </div>
                <div className="inputSymbol">－</div>
                <div className="inputName inputNameSide">
                  <div className="inputSizeNumber">
                    <input
                      type="text"
                      placeholder="4567"
                      value={state.postalCode2 || ''}
                      onChange={(e) => {
                        const value = e.target.value || '';
                        const validatedValue = value.substring(0, 4);
                        setters.setPostalCode2(validatedValue);
                      }}
                    />
                  </div>
                </div>
                <div className="inputName inputNameSide">
                  <button
                    className="classicButton"
                    onClick={async () => {
                      const res = await getAddressByZipCode(
                        state.postalCode1 + state.postalCode2,
                        prefectures,
                      );

                      if (!res) {
                        alert('対象の住所がありません。');
                        return;
                      }
                      
                      const resPrefZerofill = res.prefectureId.toString().padStart(2, '0');
                      setters.setPrefectureId(resPrefZerofill);
                      setters.setAddress1(res.address1);
                    }}
                  >
                    郵便番号検索
                  </button>
                </div>
                <div className="inputAreaSup" style={{ left: 25 }}>半角数字</div>
              </div>
            </div>
          </div>
          <div className="EntryFormBlock EntryFormBlockAlignTop">
            <label className="EntryLabel">住所</label>
            <div className="inputAreaWrapper">
              <div className="inputArea inputAreaSupInner">
                <SelectBox
                  options={prefecturesZerofill}
                  value={state.prefectureId}
                  onChange={(e) => setters.setPrefectureId(e.target.value)}
                />
              </div>
              <div className="inputAreaWrapper">
                <div className="inputArea inputAreaInner">
                  <input
                    id="input.member.address1"
                    type="text"
                    placeholder="市区町村"
                    value={state.address1 || ''}
                    onChange={(e) => setters.setAddress1(e.target.value)}
                  />
                </div>
                <div className="inputAreaSup">全角12文字、半角24字以内</div>
              </div>
              <div className="inputAreaWrapper">
                <div className="inputArea inputAreaInner">
                  <input
                    id="input.member.address2"
                    type="text"
                    placeholder="番地、アパート・マンション・ビル名"
                    value={state.address2 || ''}
                    onChange={(e) => setters.setAddress2(e.target.value)}
                  />
                </div>
                <div className="inputAreaSup">全角16文字、半角32字以内</div>
              </div>
            </div>
          </div>
        </React.Fragment>
      )}
    </React.Fragment>
  );
};

CustomerForm.defaultProps = {
  representative: false,
};

CustomerForm.propTypes = {
  id: PropTypes.string.isRequired,
  customer: PropTypes.object.isRequired,
  representative: PropTypes.bool,
  index: PropTypes.number.isRequired,
  femaleOnly: PropTypes.bool.isRequired,
  isAna2:PropTypes.bool.isRequired,
  indicatorOptions: PropTypes.array,
  sortedPlanOptionKind: PropTypes.array,
  setSortedPlanOptionKind: PropTypes.func,
};

export default CustomerForm;
